import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';
import moment from 'moment';
import {
  AdminService,
  Customer,
  Employee,
  Project,
  Unit,
  ProjectBulkCreatePayload,
} from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';
import { FeatureFlagsService } from '../../../core/services/feature-flags.service';

interface BulkParticipantRow {
  employee_id: number;
  price: number | null;
  unit_id: number;
  costcenter: string | null;
}

interface BulkTemplateRow {
  activity_name: string;
}

interface BulkCreateDraft {
  customer_id: number | null;
  project_name: string;
  external_project_name: string | null;
  endcustomer: string | null;
  extprojectid: string | null;
  ourreference: string | null;
  extreference: string | null;
  addinfo: string | null;
  invoice_lvl: string | null;
  budget_hours: number | null;
  start_date: string;
  selected_employee_ids: number[];
  participants: BulkParticipantRow[];
  activity_templates: BulkTemplateRow[];
}

@Component({
  selector: 'app-admin-bulk-create-tab',
  standalone: false,
  template: require('./admin-bulk-create-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-bulk-create-tab.component.scss').default],
})
export class AdminBulkCreateTabComponent {
  @Input() customers: Customer[] = [];
  @Input() projects: Project[] = [];
  @Input() employees: Employee[] = [];
  @Input() units: Unit[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  selectedCustomer: Customer | null = null;
  selectedProjectTemplate: Project | null = null;
  selectedEmployees: Employee[] = [];
  startDateMoment: moment.Moment | null = null;
  activeEmployees: Employee[] = [];
  availableProjectTemplates: Project[] = [];
  budgetEnabled = false;

  draft: BulkCreateDraft = this.createDefaultDraft();
  initialDraft: BulkCreateDraft = this.createDefaultDraft();
  isDirty = false;

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService,
    @Inject(FeatureFlagsService) private featureFlags: FeatureFlagsService
  ) {
    this.featureFlags.getFlags$().subscribe((flags) => {
      this.budgetEnabled = !!flags.projectBudgetInTime;
    });
  }

  get defaultUnitId(): number {
    return this.units.find((unit) => unit.description_s === 'debit')?.unit_id ?? this.units[0]?.unit_id ?? 1;
  }

  createDefaultDraft(): BulkCreateDraft {
    const today = new Date().toISOString().slice(0, 10);
    return {
      customer_id: null,
      project_name: '',
      external_project_name: null,
      endcustomer: null,
      extprojectid: null,
      ourreference: null,
      extreference: null,
      addinfo: null,
      invoice_lvl: null,
      budget_hours: null,
      start_date: today,
      selected_employee_ids: [],
      participants: [],
      activity_templates: [
        { activity_name: 'Möte' },
        { activity_name: 'Pentest' },
        { activity_name: 'Rapport' },
      ],
    };
  }

  ngOnChanges(): void {
    this.rebuildActiveEmployees();
    if (!this.selectedCustomer && this.customers.length > 0 && !this.draft.customer_id) {
      this.selectedCustomer = this.customers[0];
      this.draft.customer_id = this.selectedCustomer.customer_id;
    }
    this.rebuildAvailableProjectTemplates();
    if (!this.startDateMoment && this.draft.start_date) {
      this.startDateMoment = moment(this.draft.start_date);
    }
  }

  onCustomerChange(): void {
    this.draft.customer_id = this.selectedCustomer?.customer_id ?? null;
    this.selectedProjectTemplate = null;
    this.rebuildAvailableProjectTemplates();
    this.onFieldChange();
  }

  onProjectTemplateChange(): void {
    if (!this.selectedProjectTemplate) {
      return;
    }

    this.draft.project_name = this.selectedProjectTemplate.project_name || '';
    this.draft.endcustomer = this.selectedProjectTemplate.endcustomer || null;
    this.draft.ourreference = this.selectedProjectTemplate.ourreference || null;
    this.draft.extreference = this.selectedProjectTemplate.extreference || null;
    this.draft.extprojectid = this.selectedProjectTemplate.extprojectid || null;
    this.draft.addinfo = this.selectedProjectTemplate.addinfo || null;
    this.draft.budget_hours = null;
    this.onFieldChange();
  }

  getProjectTemplateLabel(project: Project | null | undefined): string {
    if (!project) {
      return '';
    }
    return project.endcustomer ? `${project.project_name} (${project.endcustomer})` : project.project_name;
  }

  onEmployeesChange(): void {
    const selectedIds = this.selectedEmployees.map((employee) => employee.employee_id);
    const existingByEmployeeId = new Map(this.draft.participants.map((participant) => [participant.employee_id, participant]));
    this.draft.selected_employee_ids = selectedIds;
    this.draft.participants = selectedIds.map((employeeId) => {
      const existing = existingByEmployeeId.get(employeeId);
      return (
        existing || {
          employee_id: employeeId,
          price: null,
          unit_id: this.defaultUnitId,
          costcenter: null,
        }
      );
    });
    this.onFieldChange();
  }

  getEmployeeName(employeeId: number): string {
    return this.activeEmployees.find((employee) => employee.employee_id === employeeId)?.name || String(employeeId);
  }

  addTemplateRow(): void {
    this.draft.activity_templates.push({ activity_name: '' });
    this.onFieldChange();
  }

  removeTemplateRow(index: number): void {
    if (this.draft.activity_templates.length <= 1) {
      return;
    }
    this.draft.activity_templates.splice(index, 1);
    this.onFieldChange();
  }

  onStartDateChange(newDate: moment.Moment): void {
    this.startDateMoment = newDate;
    this.draft.start_date = newDate ? newDate.format('YYYY-MM-DD') : '';
    this.onFieldChange();
  }

  onFieldChange(): void {
    this.isDirty = JSON.stringify(this.normalizedDraft(this.draft)) !== JSON.stringify(this.normalizedDraft(this.initialDraft));
  }

  resetDraft(): void {
    this.selectedCustomer = this.customers.find((customer) => customer.customer_id === this.initialDraft.customer_id) || this.customers[0] || null;
    this.selectedEmployees = this.activeEmployees.filter((employee) => this.initialDraft.selected_employee_ids.includes(employee.employee_id));
    this.draft = JSON.parse(JSON.stringify(this.initialDraft));
    this.startDateMoment = this.draft.start_date ? moment(this.draft.start_date) : null;
    this.isDirty = false;
  }

  startNew(): void {
    this.draft = this.createDefaultDraft();
    this.selectedCustomer = this.customers[0] || null;
    this.draft.customer_id = this.selectedCustomer?.customer_id ?? null;
    this.selectedEmployees = [];
    this.startDateMoment = this.draft.start_date ? moment(this.draft.start_date) : null;
    this.initialDraft = JSON.parse(JSON.stringify(this.draft));
    this.isDirty = false;
  }

  async save(): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    const projectPayload: ProjectBulkCreatePayload['project'] = {
      customer_id: this.draft.customer_id!,
      project_name: this.draft.project_name.trim(),
      external_project_name: this.nullableText(this.draft.external_project_name),
      endcustomer: this.nullableText(this.draft.endcustomer),
      extprojectid: this.nullableText(this.draft.extprojectid),
      ourreference: this.nullableText(this.draft.ourreference),
      extreference: this.nullableText(this.draft.extreference),
      addinfo: this.nullableText(this.draft.addinfo),
      invoice_lvl: this.nullableText(this.draft.invoice_lvl),
    };

    if (this.budgetEnabled) {
      projectPayload.budget_hours = this.nullablePositiveNumber(this.draft.budget_hours);
    }

    const payload: ProjectBulkCreatePayload = {
      project: {
        ...projectPayload,
      },
      participants: this.draft.participants.map((participant) => ({
        employee_id: participant.employee_id,
        price: Number(participant.price),
        unit_id: participant.unit_id,
        costcenter: participant.costcenter,
      })),
      activity_templates: this.draft.activity_templates
        .map((template) => ({ activity_name: template.activity_name.trim() }))
        .filter((template) => template.activity_name !== ''),
      start_date: this.draft.start_date,
    };

    this.adminService.bulkCreateProject(payload).subscribe({
      next: async (result) => {
        await this.dialogService.alert({
          title: 'Snabbskapa klart',
          body: `Projekt ${result.project_id} skapades och ${result.created_activities} aktiviteter lades upp.`,
          buttonText: 'OK',
          variant: 'info',
        });
        this.startNew();
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('skapa', 'projekt och aktiviteter', err),
    });
  }

  hasMissingRequiredFields(): boolean {
    const hasProject = !!this.draft.project_name.trim() && this.draft.customer_id != null;
    const hasEmployees = this.draft.participants.length > 0;
    const hasPrices = this.draft.participants.every((participant) => participant.price != null && Number(participant.price) > 0);
    const hasTemplates = this.draft.activity_templates.some((template) => template.activity_name.trim() !== '');
    const hasStartDate = !!this.draft.start_date;
    return !(hasProject && hasEmployees && hasPrices && hasTemplates && hasStartDate);
  }

  canSave(): boolean {
    return this.isDirty && !this.hasMissingRequiredFields();
  }

  canUndo(): boolean {
    return this.isDirty;
  }

  private normalizedDraft(draft: BulkCreateDraft): BulkCreateDraft {
    return {
      ...draft,
      project_name: draft.project_name.trim(),
      external_project_name: this.nullableText(draft.external_project_name),
      endcustomer: this.nullableText(draft.endcustomer),
      extprojectid: this.nullableText(draft.extprojectid),
      ourreference: this.nullableText(draft.ourreference),
      extreference: this.nullableText(draft.extreference),
      addinfo: this.nullableText(draft.addinfo),
      invoice_lvl: this.nullableText(draft.invoice_lvl),
      budget_hours: this.nullablePositiveNumber(draft.budget_hours),
      selected_employee_ids: [...draft.selected_employee_ids].sort((a, b) => a - b),
      participants: [...draft.participants]
        .map((participant) => ({ ...participant, price: participant.price == null ? null : Number(participant.price) }))
        .sort((a, b) => a.employee_id - b.employee_id),
      activity_templates: draft.activity_templates.map((template) => ({ activity_name: template.activity_name.trim() })),
    };
  }

  private nullableText(value: string | null | undefined): string | null {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  private nullablePositiveNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : parsed;
  }

  get budgetHoursInput(): string {
    return this.draft.budget_hours == null ? '' : String(this.draft.budget_hours);
  }

  set budgetHoursInput(value: string | number | null) {
    if (value === null || value === undefined || value === '') {
      this.draft.budget_hours = null;
      return;
    }
    const normalized = typeof value === 'number' ? value : value.trim();
    this.draft.budget_hours = normalized === '' ? null : Number(normalized);
  }

  hasBudgetError(): boolean {
    if (!this.budgetEnabled) {
      return false;
    }
    const value = this.draft.budget_hours;
    if (value === null || value === undefined) {
      return false;
    }
    return typeof value !== 'number' || !Number.isFinite(value) || value <= 0;
  }

  private rebuildActiveEmployees(): void {
    this.activeEmployees = this.employees
      .filter((employee) => {
        if (employee.employee_id === 0) return false;
        if (employee.stop_date) return false;
        return String(employee.contractor || '').toUpperCase() !== 'J';
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));

    if (this.selectedEmployees.length > 0) {
      const selectedIds = new Set(this.selectedEmployees.map((employee) => employee.employee_id));
      this.selectedEmployees = this.activeEmployees.filter((employee) => selectedIds.has(employee.employee_id));
    }
  }

  private rebuildAvailableProjectTemplates(): void {
    const customerId = this.selectedCustomer?.customer_id ?? this.draft.customer_id;
    this.availableProjectTemplates = this.projects
      .filter((project) => customerId != null && project.customer_id === customerId)
      .sort((a, b) => String(a.project_name || '').localeCompare(String(b.project_name || ''), 'sv'));

    if (this.selectedProjectTemplate) {
      this.selectedProjectTemplate =
        this.availableProjectTemplates.find((project) => project.project_id === this.selectedProjectTemplate?.project_id) || null;
    }
  }
}
